import { SyntheticEvent, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { spacing, fontSize } from 'presentations/views/styles';
import Tooltip from 'presentations/views/components/atoms/Tooltip';
import Icon from 'presentations/views/components/atoms/Icon';
import FloatSettingMenu, {
  MenuOptions,
} from 'presentations/views/components/molecules/FloatSettingMenu';
import {
  gray01,
  gray02,
  gray03,
  gray05,
  gray06,
  primary,
} from 'presentations/views/styles/color';

interface Props {
  iconName: string;
  options: Option[];
  selectedValue?: string | number | null;
  tooltipText: string;
  onChange?: (value: string | number | null) => void;
  isStatic?: boolean;
  noLabel?: boolean;
}

interface Option {
  icon?: string;
  iconColor?: any;
  text: string;
  value: string | number | null;
}

export default function Component(props: Props) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  const menuOptions: MenuOptions[] = props.options.map((option) => ({
    icon: option.icon,
    iconColor: option.iconColor,
    label: option.text,
  }));
  const selectedOption =
    !!props.selectedValue &&
    props.options.find((option) => option.value === props.selectedValue);

  const onClickMenu = (e: SyntheticEvent<HTMLDivElement>) => {
    if (!props.onChange) return;
    setAnchor(e.currentTarget);
  };
  const onSelectMenu = (index: number) => {
    setAnchor(null);
    props.onChange && props.onChange(props.options[index].value);
  };
  const onCloseMenu = () => {
    setAnchor(null);
  };

  if (props.isStatic) {
    if (!selectedOption) return null;
    return (
      <StaticItem>
        <MetaIcon
          name={props.iconName}
          size="14px"
          styledColor={selectedOption.iconColor}
        />
        {!props.noLabel && selectedOption.text}
      </StaticItem>
    );
  }

  return (
    <>
      <Tooltip title={props.tooltipText} placement="top">
        {selectedOption ? (
          <MetaTagItem onClick={onClickMenu} selectable={!!props.onChange}>
            <MetaIcon
              name={props.iconName}
              size="14px"
              styledColor={selectedOption.iconColor}
            />
            {selectedOption.text}
            {props.onChange && <ChangeIcon name="chevron-down" size="20px" />}
          </MetaTagItem>
        ) : (
          props.onChange && (
            <UnselectItem onClick={onClickMenu} selectable={!!props.onChange}>
              <Icon name={props.iconName} size="16px" />
            </UnselectItem>
          )
        )}
      </Tooltip>

      {props.onChange && (
        <FloatSettingMenu
          onSelectMenu={onSelectMenu}
          onCloseMenu={onCloseMenu}
          anchor={anchor}
          options={menuOptions}
        />
      )}
    </>
  );
}

const MetaTagItem = styled.div<{ selectable?: boolean }>`
  display: flex;
  align-items: center;
  height: 28px;
  font-size: ${fontSize(13)};
  padding: ${spacing(4)} ${spacing(8)};
  border-radius: 100px;
  background-color: ${gray06};
  color: ${gray01};
  cursor: ${(props) => props.selectable && 'pointer'};
  :hover {
    background-color: ${(props) => props.selectable && gray05};
  }
`;
const StaticItem = styled.div`
  display: flex;
  align-items: center;
  font-size: ${fontSize(13)};
  color: ${gray01};
`;
const MetaIcon = styled(Icon)<{ styledColor?: (props: any) => string }>`
  margin-right: ${spacing(2)};
  color: ${(props) => props.styledColor};
`;
const ChangeIcon = styled(Icon)``;

const UnselectItem = styled.div<{ selectable?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: ${spacing(6)};
  border-radius: 100px;
  border: 1px dashed ${gray03};
  color: ${gray02};

  ${(props) =>
    props.selectable &&
    css`
      cursor: pointer;
      &:hover {
        border-color: ${primary(props)};
        color: ${primary(props)};
      }
    `}
`;
